import {
  PermissionsGroupName,
  SalesOrderPermissions,
} from '@order-management/roles';
import {
  DeferredResponse,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SalesOrderData,
  SalesOrdersTableRow,
  SortDirection,
  User,
  UserSalesOrdersCriteria,
} from '@order-management/types';
import {
  MouseEvent,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Columns, Element, Icon } from 'react-bulma-components';
import { FaFileInvoice, FaPenToSquare, FaUser, FaXmark } from 'react-icons/fa6';
import {
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigate,
} from 'react-router-dom';
import SalesOrdersTable from '../../sales-orders/sales-orders-table/sales-orders-table';
import BackButton from '../../shared/back-button/back-button';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import { useHttpRequest } from '../../shared/hooks/http-request';
import useIsUserEditable from '../../shared/hooks/is-user-editable';
import useUser from '../../shared/hooks/user';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import DetailsCard from '../../ui/details-card/details-card';
import FilterTagList from '../../ui/filter-tag-list/filter-tag-list';
import InfoLabel from '../../ui/info-label/info-label';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import { fetchUser, fetchUserSalesOrders } from '../users-service';
import { userDetailsSalesOrderDetailsColumns } from './constants';
import styles from './user-details-page.module.scss';

type selectablePermissionType = {
  label: string;
  permissionName: PermissionsGroupName;
  selected: boolean;
};

const PERMISSIONS_GROUP_LABELS = {
  [PermissionsGroupName.END_USER_SOFTWARE_CONTACT]: 'End User Software Contact',
  [PermissionsGroupName.BILLING_CONTACT]: 'Billing Contact',
  [PermissionsGroupName.SHIPPING_CONTACT]: 'Shipping Contact',
  [PermissionsGroupName.SALES_ORDER_MANAGER]: 'Manager',
  [PermissionsGroupName.VIEW_ONLY_SALES_ORDER_MANAGER]: 'Viewer',
  [PermissionsGroupName.TEMPORARY_SALES_ORDER_MANAGER]: 'Manager - Temporary',
};

export const loader: LoaderFunction = async ({ params }) => {
  const id = params.id;

  const response = fetchUser(id!);

  return defer({ response });
};

const initialUserSalesOrdersCriteria: UserSalesOrdersCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: 'number',
  sortDirection: SortDirection.DES,
};

const FETCH_USER_SALES_ORDERS_ERROR_MESSAGE = '';

export function UserDetailsPageContent() {
  const navigate = useNavigate();
  const user = useAsyncValue() as User;
  const { user: currentUser } = useUser();
  const [relatedSalesOrder, setRelatedSalesOrder] = useState<
    PaginatedAPIResponse<SalesOrdersTableRow>
  >({
    data: [],
    count: 0,
    page: 0,
    total: 0,
    pages: 0,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<
    Map<PermissionsGroupName, boolean>
  >(new Map());
  const { executeHttpRequest, isLoading: isLoadingUserSalesOrders } =
    useHttpRequest();
  const [salesOrdersTableCriteria, setSalesOrdersTableCriteria] =
    useState<UserSalesOrdersCriteria>(initialUserSalesOrdersCriteria);

  const { isEditableBySalesOrderRelatedUser } = useIsUserEditable();
  const permissionsFilter: selectablePermissionType[] = useMemo(() => {
    return [
      {
        label:
          PERMISSIONS_GROUP_LABELS[
            PermissionsGroupName.END_USER_SOFTWARE_CONTACT
          ],
        permissionName: PermissionsGroupName.END_USER_SOFTWARE_CONTACT,
        selected: !!selectedPermissions.get(
          PermissionsGroupName.END_USER_SOFTWARE_CONTACT
        ),
      },
      {
        label: PERMISSIONS_GROUP_LABELS[PermissionsGroupName.BILLING_CONTACT],
        permissionName: PermissionsGroupName.BILLING_CONTACT,
        selected: !!selectedPermissions.get(
          PermissionsGroupName.BILLING_CONTACT
        ),
      },
      {
        label: PERMISSIONS_GROUP_LABELS[PermissionsGroupName.SHIPPING_CONTACT],
        permissionName: PermissionsGroupName.SHIPPING_CONTACT,
        selected: !!selectedPermissions.get(
          PermissionsGroupName.SHIPPING_CONTACT
        ),
      },
      {
        label:
          PERMISSIONS_GROUP_LABELS[PermissionsGroupName.SALES_ORDER_MANAGER],
        permissionName: PermissionsGroupName.SALES_ORDER_MANAGER,
        selected: !!selectedPermissions.get(
          PermissionsGroupName.SALES_ORDER_MANAGER
        ),
      },
      {
        label:
          PERMISSIONS_GROUP_LABELS[
            PermissionsGroupName.VIEW_ONLY_SALES_ORDER_MANAGER
          ],
        permissionName: PermissionsGroupName.VIEW_ONLY_SALES_ORDER_MANAGER,
        selected: !!selectedPermissions.get(
          PermissionsGroupName.VIEW_ONLY_SALES_ORDER_MANAGER
        ),
      },
      {
        label:
          PERMISSIONS_GROUP_LABELS[
            PermissionsGroupName.TEMPORARY_SALES_ORDER_MANAGER
          ],
        permissionName: PermissionsGroupName.TEMPORARY_SALES_ORDER_MANAGER,
        selected: !!selectedPermissions.get(
          PermissionsGroupName.TEMPORARY_SALES_ORDER_MANAGER
        ),
      },
    ];
  }, [selectedPermissions]);

  const filterTagsData = useMemo(() => {
    return Object.fromEntries(
      Object.entries(PERMISSIONS_GROUP_LABELS).map(
        ([permissionName, label]) => [
          permissionName,
          {
            label: 'Permissions',
            value: salesOrdersTableCriteria.userPermissionsGroups?.includes(
              permissionName as PermissionsGroupName
            )
              ? label
              : '',
          },
        ]
      )
    );
  }, [salesOrdersTableCriteria.userPermissionsGroups]);

  const appliedFilters = useMemo(() => {
    return Object.entries(filterTagsData).filter(
      ([_, { value }]) => value.length > 0
    );
  }, [filterTagsData]);

  const fetchRelatedSalesOrder = useCallback(
    async () =>
      executeHttpRequest({
        asyncFunction: async () => {
          const salesOrders = await fetchUserSalesOrders(
            user.id,
            salesOrdersTableCriteria
          );
          setRelatedSalesOrder(salesOrders);
        },
        customErrorMessage: FETCH_USER_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeHttpRequest, salesOrdersTableCriteria, user.id]
  );

  useEffect(() => {
    fetchRelatedSalesOrder();
  }, [fetchRelatedSalesOrder, user]);

  const salesOrderTableConfig: PaginatedContentConfig = useMemo(
    () => ({ ...salesOrdersTableCriteria, pages: relatedSalesOrder.pages }),
    [relatedSalesOrder.pages, salesOrdersTableCriteria]
  );

  const emptySalesOrdersContentText = useMemo(() => {
    const hasAppliedFilters =
      salesOrdersTableCriteria.search !== '' ||
      (salesOrdersTableCriteria.userPermissionsGroups ?? []).length > 0;
    if (hasAppliedFilters) {
      return 'There are no sales orders that match the selected criteria';
    }
    return user.id === currentUser.id
      ? `You don't have any sales orders associated`
      : 'There are no sales orders at the moment';
  }, [
    currentUser.id,
    salesOrdersTableCriteria.search,
    salesOrdersTableCriteria.userPermissionsGroups,
    user.id,
  ]);

  const handleOnSalesOrdersTableChange = useCallback(
    (value: OnTableChangeData<SalesOrdersTableRow>) => {
      const newData = {
        ...salesOrdersTableCriteria,
        ...value.config,
        sortBy: value.column?.key || salesOrdersTableCriteria.sortBy,
      };
      setSalesOrdersTableCriteria(newData);
    },
    [salesOrdersTableCriteria]
  );

  const handleOnPermissionsFilterChange = useCallback(
    (col: selectablePermissionType) => {
      setSelectedPermissions(
        new Map(selectedPermissions.set(col.permissionName, !col.selected))
      );
      const newData = {
        ...salesOrdersTableCriteria,
        userPermissionsGroups: permissionsFilter
          .filter((col) => selectedPermissions.get(col.permissionName))
          .map((col) => col.permissionName),
      };
      setSalesOrdersTableCriteria(newData);
    },
    [permissionsFilter, salesOrdersTableCriteria, selectedPermissions]
  );

  const onRemoveFilterTag = useCallback(
    (filterItem: PermissionsGroupName) => {
      const newData = {
        ...salesOrdersTableCriteria,
        userPermissionsGroups:
          salesOrdersTableCriteria.userPermissionsGroups?.filter(
            (permissionsGroup) => permissionsGroup !== filterItem
          ),
      };
      setSelectedPermissions(
        new Map(selectedPermissions.set(filterItem, false))
      );
      setSalesOrdersTableCriteria(newData);
    },
    [salesOrdersTableCriteria, selectedPermissions]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4"
      header={
        <Element className="p-4">
          <BackButton />
          <h1 className="is-size-3 has-text-weight-light">{user.email}</h1>
        </Element>
      }
    >
      <Columns className="p-4">
        <Columns display="flex" flexDirection="column" className={`pr-7`}>
          <Columns.Column>
            <DetailsCard title="Basic information">
              <InfoLabel
                icon={<FaUser />}
                label="First Name"
                content={user.firstName}
              />
              <InfoLabel
                icon={<FaUser />}
                label="Last Name"
                content={user.lastName}
              />
            </DetailsCard>
          </Columns.Column>
        </Columns>
        <Columns.Column className={`pt-0 ${styles.tableColumn}`}>
          <DetailsCard>
            <>
              <section className="is-flex is-align-items-center">
                <Icon color="primary" mr={2}>
                  <FaFileInvoice size={24} />
                </Icon>
                <span className="is-size-4 has-text-weight-light">
                  Related Sales Orders
                </span>
                <If condition={isEditableBySalesOrderRelatedUser(user)}>
                  <Button
                    ml={1}
                    text
                    className={`has-text-primary-dark is-size-7 `}
                    onClick={() => {
                      navigate(`/users/my-team/${user.id}/edit`);
                    }}
                  >
                    <Icon>
                      <FaPenToSquare />
                    </Icon>
                    <span>Edit</span>
                  </Button>
                </If>
              </section>
              <section className={styles.filtersSection}>
                {permissionsFilter.map((col) => (
                  <Button
                    key={col.permissionName}
                    type="button"
                    color="primary-dark"
                    outlined={!col.selected}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                      e.currentTarget.blur();
                      handleOnPermissionsFilterChange(col);
                    }}
                  >
                    <Conditional condition={col.selected}>
                      <Conditional.True>
                        <Icon>
                          <FaXmark />
                        </Icon>
                      </Conditional.True>
                    </Conditional>
                    <span>{col.label}</span>
                  </Button>
                ))}
              </section>
              <If condition={appliedFilters.length > 0}>
                <Element mb={5}>
                  <FilterTagList
                    list={filterTagsData}
                    onRemove={(item) =>
                      onRemoveFilterTag(item as PermissionsGroupName)
                    }
                    title="Filters"
                  />
                </Element>
              </If>
              <Conditional condition={isLoadingUserSalesOrders}>
                <Conditional.True>
                  <PageSpinner />
                </Conditional.True>
                <Conditional.False>
                  <SalesOrdersTable
                    columns={userDetailsSalesOrderDetailsColumns}
                    dataSource={relatedSalesOrder.data}
                    config={salesOrderTableConfig}
                    onChange={handleOnSalesOrdersTableChange}
                    empty={
                      <Element
                        display="flex"
                        justifyContent="center"
                        className="my-2"
                      >
                        <span className="has-text-weight-light">
                          {emptySalesOrdersContentText}
                        </span>
                      </Element>
                    }
                  />
                </Conditional.False>
              </Conditional>
            </>
          </DetailsCard>
        </Columns.Column>
      </Columns>
    </BoxLayout>
  );
}

export function UserDetailsPage() {
  const { response } = useLoaderData() as DeferredResponse<SalesOrderData>;

  return (
    <ProtectedPage
      permissions={SalesOrderPermissions.SALES_ORDERS_SUMMARY_READ}
    >
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <UserDetailsPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default UserDetailsPage;
